import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { AppLayout } from './components/AppLayout';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { CustomersPage } from './pages/CustomersPage';
import { ArticlesPage } from './pages/ArticlesPage';
import { PriceListsPage } from './pages/PriceListsPage';
import { OrdersPage } from './pages/OrdersPage';
import { DocumentsPage } from './pages/DocumentsPage';
import { OpenItemsPage } from './pages/OpenItemsPage';
import { ZahlungseingaengePage } from './pages/ZahlungseingaengePage';
import { ProfilePage } from './pages/ProfilePage';
import { SLOTS } from './lib/settingsNav';

// Vollanalyse M18: Code-Splitting — die schweren Seiten laden erst beim ersten
// Aufruf ihrer Route (eigener Chunk je Seite statt eines 1,5-MB-Initial-Bundles;
// recharts und die Plantafel hängen damit nicht mehr an der LoginPage).
// TanStack Router v1: `lazyRouteComponent(importer, exportName)` — das zweite
// Argument wählt den benannten Export aus dem dynamisch geladenen Modul.
// B4 (Optimierung 2026-08-25): auch die selten besuchten bzw. rein
// dokumentarischen Seiten laden lazy — zusammen ~45 kB Quelle weniger im
// Haupt-Chunk (Hilfe allein 801 Zeilen statischer Text).
const PriceListDetailPage = lazyRouteComponent(
  () => import('./pages/PriceListDetailPage'),
  'PriceListDetailPage',
);
const DunningPage = lazyRouteComponent(() => import('./pages/DunningPage'), 'DunningPage');
const RecurringPage = lazyRouteComponent(() => import('./pages/RecurringPage'), 'RecurringPage');
const ActivityPage = lazyRouteComponent(() => import('./pages/ActivityPage'), 'ActivityPage');
const DevicesPage = lazyRouteComponent(() => import('./pages/DevicesPage'), 'DevicesPage');
const TicketsPage = lazyRouteComponent(() => import('./pages/TicketsPage'), 'TicketsPage');
const SupportPage = lazyRouteComponent(() => import('./pages/TicketsPage'), 'SupportPage');
const HelpPage = lazyRouteComponent(() => import('./pages/HelpPage'), 'HelpPage');

const CustomerFilePage = lazyRouteComponent(
  () => import('./pages/CustomerFilePage'),
  'CustomerFilePage',
);
const OrderDetailPage = lazyRouteComponent(
  () => import('./pages/OrderDetailPage'),
  'OrderDetailPage',
);
const DocumentEditorPage = lazyRouteComponent(
  () => import('./pages/DocumentEditorPage'),
  'DocumentEditorPage',
);
const QuotesPage = lazyRouteComponent(() => import('./pages/QuotesPage'), 'QuotesPage');
const AnalyticsPage = lazyRouteComponent(() => import('./pages/AnalyticsPage'), 'AnalyticsPage');
const AttendancePage = lazyRouteComponent(() => import('./pages/AttendancePage'), 'AttendancePage');
const PlantafelPage = lazyRouteComponent(() => import('./pages/PlantafelPage'), 'PlantafelPage');
const MaterialPage = lazyRouteComponent(() => import('./pages/MaterialPage'), 'MaterialPage');
const DataExchangePage = lazyRouteComponent(
  () => import('./pages/DataExchangePage'),
  'DataExchangePage',
);
const UsersPage = lazyRouteComponent(() => import('./pages/UsersPage'), 'UsersPage');
const RolesPage = lazyRouteComponent(() => import('./pages/RolesPage'), 'RolesPage');
const SettingsPage = lazyRouteComponent(() => import('./pages/SettingsPage'), 'SettingsPage');

const rootRoute = createRootRoute({ component: () => <Outlet /> });

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  // A2-6: `?redirect=<href>` remembers where a 401 interrupted the user so the
  // login can send them back.
  validateSearch: (s: Record<string, unknown>): { redirect?: string } => ({
    redirect: typeof s.redirect === 'string' ? s.redirect : undefined,
  }),
  component: LoginPage,
});

/** Authenticated area: shell with navigation, session guard inside AppLayout. */
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_app',
  component: () => (
    <AppLayout>
      <Outlet />
    </AppLayout>
  ),
});

const indexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/',
  component: DashboardPage,
});

const customersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/customers',
  component: CustomersPage,
  // `neu=1` von den Schnellknoepfen der Uebersicht.
  validateSearch: (s: Record<string, unknown>): { neu?: string } => ({
    neu: s.neu === '1' ? '1' : undefined,
  }),
});

const customerFileRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/customers/$customerId',
  component: CustomerFilePage,
  // Sprung aus der Suche auf eine Wohnung (Faruq 2026-08-24): das Gebaeude
  // wird aufgeklappt, die Zeile kurz hervorgehoben.
  validateSearch: (s: Record<string, unknown>): { propertyId?: string; unitId?: string } => ({
    propertyId: typeof s.propertyId === 'string' ? s.propertyId : undefined,
    unitId: typeof s.unitId === 'string' ? s.unitId : undefined,
  }),
});

const articlesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/articles',
  component: ArticlesPage,
});

const priceListsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/price-lists',
  component: PriceListsPage,
});

const ordersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/orders',
  component: OrdersPage,
  // Kundenakte-Chips springen mit einem Kundenfilter hierher; `neu=1` kommt
  // von den Schnellknoepfen der Uebersicht und oeffnet gleich den Anlegen-Dialog.
  validateSearch: (s: Record<string, unknown>): { customerId?: string; neu?: string } => ({
    customerId: typeof s.customerId === 'string' ? s.customerId : undefined,
    neu: s.neu === '1' ? '1' : undefined,
  }),
});

const orderDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/orders/$orderId',
  component: OrderDetailPage,
});

const documentsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/documents',
  component: DocumentsPage,
  // Kundenakte-Chips springen mit Kundenfilter (+ Belegart-Tab) hierher.
  // Tobias 2026-09-10: ALLE Filter des Journals leben in der URL — nach dem
  // Klick in einen Beleg bringt „Zurück" im Browser genau die Filter wieder.
  validateSearch: (
    s: Record<string, unknown>,
  ): { customerId?: string; type?: string; status?: string; q?: string; draft?: boolean } => ({
    customerId: typeof s.customerId === 'string' ? s.customerId : undefined,
    type: typeof s.type === 'string' ? s.type : undefined,
    status: typeof s.status === 'string' ? s.status : undefined,
    q: typeof s.q === 'string' && s.q ? s.q : undefined,
    // Boolean statt „1": der Router schreibt Strings, die wie Zahlen aussehen, in Anfuehrungszeichen.
    draft: s.draft === true || s.draft === '1' || s.draft === 'true' ? true : undefined,
  }),
});

const documentEditorRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/documents/$documentId',
  component: DocumentEditorPage,
});

const priceListDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/price-lists/$priceListId',
  component: PriceListDetailPage,
});

const openItemsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/open-items',
  component: OpenItemsPage,
});

// Zahlungseingänge (Faruq 2026-08-29): Kontoauszüge einlesen, eindeutige
// Zahlungen automatisch verbuchen, den Rest dem Büro zur Entscheidung vorlegen.
const zahlungseingaengeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/payments-in',
  component: ZahlungseingaengePage,
});

// Datenaustausch (D-M0-K3): DATEV-Export und Sage-Import unter einer Route mit
// zwei Tabs. Die alten Pfade bleiben als Weiterleitung erhalten (Deep-Links,
// Drehbuch), setzen aber den passenden Tab über `?tab=`.
const dataExchangeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/datenaustausch',
  validateSearch: (s: Record<string, unknown>): { tab?: 'datev' | 'datanorm' | 'lv' } => ({
    tab:
      s.tab === 'datanorm'
        ? 'datanorm'
        : s.tab === 'lv'
          ? 'lv'
          : s.tab === 'datev'
            ? 'datev'
            : undefined,
  }),
  component: DataExchangePage,
});

const datevRedirectRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/datev',
  beforeLoad: () => {
    throw redirect({ to: '/datenaustausch', search: { tab: 'datev' } });
  },
});

const materialRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/material',
  component: MaterialPage,
});

const plantafelRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/plantafel',
  component: PlantafelPage,
  // Faruq 2026-09-05: „Einplanen" von der Auftragsseite — Arbeitsvorrat auf die
  // Nummer filtern und, wenn schon verplant, die Woche des naechsten Einsatzes zeigen.
  validateSearch: (s: Record<string, unknown>): { auftrag?: string; woche?: string } => ({
    auftrag: typeof s.auftrag === 'string' && s.auftrag ? s.auftrag : undefined,
    woche: typeof s.woche === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s.woche) ? s.woche : undefined,
  }),
});

const dunningRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/dunning',
  component: DunningPage,
});

const quotesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/quotes',
  component: QuotesPage,
  // `neu=1` von den Schnellknoepfen der Uebersicht.
  validateSearch: (s: Record<string, unknown>): { neu?: string } => ({
    neu: s.neu === '1' ? '1' : undefined,
  }),
});

const recurringRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/recurring',
  component: RecurringPage,
});

const analyticsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/analytics',
  component: AnalyticsPage,
});

const activityRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/activity',
  component: ActivityPage,
});

const attendanceRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/attendance',
  component: AttendancePage,
});

const usersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/users',
  component: UsersPage,
});

const rolesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/roles',
  component: RolesPage,
});

// Selbstverwaltung (Nutzer-Menü): Profil und Hilfe — für jede Rolle erreichbar.
const profileRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/profile',
  component: ProfilePage,
});

const helpRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/help',
  component: HelpPage,
});

// Ticketsystem (Faruq 2026-08-24): ohne Guard — melden darf jeder, die
// Sichtbarkeit der einzelnen Tickets regelt der Server. ZWEI Routen auf
// dieselbe Seite, weil es zwei Blickwinkel sind: `/tickets` ist die
// Warteschlange der Systembetreuung, `/support` sind die EIGENEN Meldungen.
// Eigene Adressen statt eines Schalters, damit die Seitenleiste nicht zwei
// Eintraege gleichzeitig hervorhebt.
const ticketsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/tickets',
  component: TicketsPage,
});

const supportRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/support',
  component: SupportPage,
});

// P2-M12: gerätneutrale Geräteverwaltung (gekoppelte Monteur-App-Geräte).
const devicesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/devices',
  component: DevicesPage,
});

const settingsIndexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings',
  beforeLoad: () => {
    throw redirect({ to: '/settings/$tab', params: { tab: 'betrieb' } });
  },
});

// Einstellungen mit aktivem Reiter in der URL (P1-B/B), z. B. /settings/datev.
// D-W1 (gesetztes Muster §1): der aktive Texte-Slot liegt zusätzlich als
// Query-Parameter in der URL (/settings/texte?slot=MAIL_BODY) — analog zum
// `?tab=`-Muster des Datenaustauschs; unbekannte Werte fallen auf den
// Standard-Slot zurück.
const settingsTabRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/$tab',
  validateSearch: (s: Record<string, unknown>): { slot?: string } => ({
    slot: SLOTS.some((slot) => slot.value === s.slot) ? (s.slot as string) : undefined,
  }),
  component: SettingsPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  appRoute.addChildren([
    indexRoute,
    customersRoute,
    customerFileRoute,
    articlesRoute,
    priceListsRoute,
    priceListDetailRoute,
    ordersRoute,
    orderDetailRoute,
    documentsRoute,
    documentEditorRoute,
    openItemsRoute,
    zahlungseingaengeRoute,
    materialRoute,
    plantafelRoute,
    dunningRoute,
    quotesRoute,
    recurringRoute,
    attendanceRoute,
    analyticsRoute,
    activityRoute,
    dataExchangeRoute,
    datevRedirectRoute,
    usersRoute,
    rolesRoute,
    profileRoute,
    helpRoute,
    ticketsRoute,
    supportRoute,
    devicesRoute,
    settingsIndexRoute,
    settingsTabRoute,
  ]),
]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
